
import { 
  ChartContainer, 
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent
} from "@/components/ui/chart";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { Category } from "@/types/assessment";
import { useChartConfig } from "@/hooks/useChartConfig";
import { useChartData } from "@/hooks/useChartData";
import { useEffect, useState } from "react";

interface AssessmentResultsChartProps {
  categories: Category[];
  scoresByCategory: Record<string, {current: number, target: number}>;
}

const AssessmentResultsChart = ({ categories, scoresByCategory }: AssessmentResultsChartProps) => {
  const chartConfig = useChartConfig();
  const [effectiveScores, setEffectiveScores] = useState(scoresByCategory);
  
  // Try to load scores from localStorage if the provided scores are empty
  useEffect(() => {
    if (Object.keys(scoresByCategory).length === 0) {
      const savedScores = localStorage.getItem('assessment-results-scores');
      if (savedScores) {
        try {
          const parsedScores = JSON.parse(savedScores);
          setEffectiveScores(parsedScores);
        } catch (error) {
          // Error parsing saved scores
        }
      }
    } else {
      setEffectiveScores(scoresByCategory);
    }
  }, [scoresByCategory]);
  
  const chartData = useChartData(categories, effectiveScores);
  
  // Don't render the chart if there's no valid data at all
  const hasData = chartData.length > 0;

  if (!hasData) {
    return (
      <div className="w-full aspect-square max-w-xl mx-auto mt-2 mb-6 flex items-center justify-center">
        <p className="text-muted-foreground">No chart data available</p>
      </div>
    );
  }

  return (
    <div className="w-full aspect-square max-w-xl mx-auto mt-2 mb-6">
      <h3 className="text-lg font-medium text-center mb-4">Assessment Results</h3>
      <ChartContainer 
        config={chartConfig} 
        className="w-full h-full"
      >
        <RadarChart 
          cx="50%" 
          cy="54%" 
          outerRadius="60%" 
          data={chartData}
          margin={{ top: 60, right: 80, bottom: 40, left: 80 }}
        >
          <PolarGrid gridType="circle" stroke="#e0e0e0" />
          <PolarAngleAxis 
            dataKey="name"
            tick={{ fill: '#403E43', fontSize: 12, fontWeight: 500, dy: -8 }}
            axisLine={{ strokeWidth: 2 }}
            tickLine={false}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, 4]} 
            tickCount={5} 
            tick={{ fill: '#8A898C' }}
            stroke="#e0e0e0"
            axisLine={false}
          />
          
          <Radar 
            name="Current" 
            dataKey="current" 
            stroke="#8E9196" 
            fill="#8E9196" 
            fillOpacity={0.3} 
            dot={true}
            activeDot={{ r: 4 }}
            isAnimationActive={true}
          />
          
          <Radar 
            name="Target" 
            dataKey="target" 
            stroke="#e87a2e" 
            fill="#e87a2e" 
            fillOpacity={0.5}
            dot={true}
            activeDot={{ r: 4 }}
            isAnimationActive={true} 
          />
          
          <ChartTooltip content={<ChartTooltipContent />} />
          
          <ChartLegend verticalAlign="bottom">
            <ChartLegendContent />
          </ChartLegend>
        </RadarChart>
      </ChartContainer>
    </div>
  );
};

export default AssessmentResultsChart;
